"use client";

import { CookieConsent } from "@/lib/consent";
import { GlassPanel } from "@/components/ui/GlassPanel";

type CookiePreferencesModalProps = {
  consent: CookieConsent;
  onChange: (consent: CookieConsent) => void;
  onSave: () => void;
  onClose: () => void;
};

export function CookiePreferencesModal({ consent, onChange, onSave, onClose }: CookiePreferencesModalProps) {
  const toggle = (key: "preferences" | "analytics" | "marketing") => {
    onChange({ ...consent, [key]: !consent[key] });
  };

  return (
    <GlassPanel
      variant="bordered"
      className="pointer-events-auto w-full max-w-md rounded-3xl border-border bg-surface/90 p-5 text-sm text-offwhite/80 shadow-lg"
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-emerald">Cookie preferences</p>
          <h2 className="mt-2 text-2xl font-semibold text-offwhite">Choose how we use cookies</h2>
          <p className="mt-2 text-sm text-offwhite/70">
            Control optional cookies at any time. Necessary cookies keep the site secure and functional.
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="text-xs uppercase tracking-[0.3em] text-offwhite/50 hover:text-offwhite"
        >
          Close
        </button>
      </div>

      <div className="mt-5 space-y-4">
        <label className="flex items-center justify-between gap-3 rounded-2xl border border-border-subtle bg-card/60 p-4">
          <div>
            <p className="text-sm font-semibold text-offwhite">Necessary</p>
            <p className="text-xs text-offwhite/60">Required for security, login, and basic site functions.</p>
          </div>
          <input type="checkbox" checked readOnly className="h-5 w-5 accent-emerald" />
        </label>

        {(["preferences", "analytics", "marketing"] as const).map((category) => (
          <label
            key={category}
            className="flex items-center justify-between gap-3 rounded-2xl border border-border-subtle bg-card/60 p-4"
          >
            <div>
              <p className="text-sm font-semibold capitalize text-offwhite">{category}</p>
              <p className="text-xs text-offwhite/60">
                {category === "preferences" && "Remember your settings and experience choices."}
                {category === "analytics" && "Help us understand usage to improve the experience."}
                {category === "marketing" && "Enable relevant offers and communications."}
              </p>
            </div>
            <input
              type="checkbox"
              checked={consent[category]}
              onChange={() => toggle(category)}
              className="h-5 w-5 accent-emerald"
            />
          </label>
        ))}
      </div>

      <div className="mt-5 flex flex-wrap gap-3">
        <button
          type="button"
          onClick={onSave}
          className="btn-premium inline-flex items-center justify-center rounded-full bg-emerald/90 px-4 py-2 text-xs font-semibold text-midnight shadow-emerald/20 hover:bg-emerald"
        >
          Save preferences
        </button>
        <button
          type="button"
          onClick={onClose}
          className="capsule-link text-xs uppercase tracking-[0.3em] text-offwhite/60 hover:text-offwhite"
        >
          Cancel
        </button>
      </div>
    </GlassPanel>
  );
}
